import { useState } from "react";
import API from "@/lib/api";
import { Send, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";

/**
 * One-click "Nudge patient to upload ID" button. Used inline in the
 * Prescriber Order Queue next to the "No ID" badge.
 *
 * Behaviour:
 *   - On click → POST /api/admin/users/{pid}/id-verification/nudge
 *   - Backend sends BOTH an SMS and an email (best-effort, returns per-channel result)
 *   - Backend enforces a 6-hour cooldown — second click within window → 429
 *   - On cooldown, render greyed-out "Nudged Xh ago" pill instead of the active button
 *
 * Surface for the clinician:
 *   - Active: rose pill with paper-plane icon + "Nudge"
 *   - In-flight: spinner + "Sending…"
 *   - Recently sent: slate pill with clock + "Nudged Xh ago" (disabled)
 */
export default function IdNudgeButton({ orderId, patientId, lastNudgedAt, onSent }) {
  const [sending, setSending] = useState(false);

  // Cooldown evaluation — backend enforces 6 hours; we mirror the
  // calculation so the UI flips to "Nudged" immediately on success
  // (so the clinician doesn't accidentally re-click before the next
  // queue refresh). Race condition: if our clock is wildly off we'll
  // simply see a 429 toast — harmless.
  let cooldownLeftMs = 0;
  if (lastNudgedAt) {
    try {
      const last = new Date(lastNudgedAt).getTime();
      const elapsed = Date.now() - last;
      cooldownLeftMs = Math.max(0, 6 * 3600 * 1000 - elapsed);
    } catch {
      cooldownLeftMs = 0;
    }
  }
  const onCooldown = cooldownLeftMs > 0;

  const send = async () => {
    setSending(true);
    try {
      const { data } = await API.post(`/admin/users/${patientId}/id-verification/nudge`);
      const sms_ok = data?.sms?.sent;
      const email_ok = data?.email?.sent;
      if (sms_ok && email_ok) toast.success("Nudged via SMS + email");
      else if (sms_ok) toast.success("Nudged via SMS (no email on file)");
      else if (email_ok) toast.success("Nudged via email (no phone on file)");
      else toast.warning("Nudge attempted but neither channel confirmed delivery");
      onSent?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Nudge failed";
      // 429 → cooldown still active; show as info rather than error.
      if (e?.response?.status === 429) toast.info(detail);
      else toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  if (onCooldown) {
    const hoursLeft = Math.round(cooldownLeftMs / 3600 / 100) / 10 || (cooldownLeftMs / 3600 / 1000).toFixed(1);
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] bg-slate-200 dark:bg-slate-800 text-slate-500 border border-slate-300 dark:border-slate-700 px-2 py-0.5 rounded-full font-medium cursor-not-allowed"
        title={`Already nudged this patient recently. Cooldown ends in ${hoursLeft}h.`}
        data-testid={`id-nudge-cooldown-${orderId}`}
      >
        <Clock className="w-3 h-3" /> Nudged {hoursLeft}h ago
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={send}
      disabled={sending}
      title="Send the patient an SMS + email reminding them to complete ID verification"
      className="inline-flex items-center gap-1 text-[10px] bg-rose-600/10 hover:bg-rose-600/20 text-rose-700 dark:text-rose-300 border border-rose-400 dark:border-rose-700/60 px-2 py-0.5 rounded-full font-semibold transition-colors disabled:opacity-50"
      data-testid={`id-nudge-btn-${orderId}`}
    >
      {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
      {sending ? "Sending…" : "Nudge"}
    </button>
  );
}
